import React, { useState, useEffect } from 'react';
import { getConsentBranding } from '../../utils/consentBranding';
import {
  addLead,
  addCustomer,
  getOrganizations,
  getTeamsByOrganization,
  getUserSubTeamInfo,
  sendEmail,
  getLeadTemplates,
  saveLeadTemplate,
  deleteLeadTemplate
} from '../../api';
import { useNavigate, useLocation } from 'react-router-dom';
import SignatureField from '../../components/SignatureField';
import OptInToggle from '../../components/common/OptInToggle';
import LeadTemplateModal from '../../components/common/LeadTemplateModal';
import PvConsumptionQuestions from '../../components/forms/PvConsumptionQuestions';
import {
  hasQualifyingPvConsumption,
  isPvConsumptionStepComplete,
  isPvConsumptionDisqualified,
  getPvConsumptionSubmitValues,
  isYearlyConsumptionOver2700Valid
} from '../../utils/pvConsumptionQualification';

function LeadForm({ user, onCancel, onLeadAdded }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const customerId = queryParams.get('customerId');
  const [step, setStep] = useState(1);
  const [organizationName, setOrganizationName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [asbestosTab, setAsbestosTab] = useState('unknown');
  const [monumentTab, setMonumentTab] = useState('unknown');
  const [propertyData, setPropertyData] = useState({
    isOwner: null, // null = nicht ausgewählt, true/false für Ja/Nein
    buildingType: '', // 'Einfamilienhaus', 'Zweifamilienhaus', 'Reihenhaus'
    roofOrientation: '', // GEÄNDERT: Leerstring statt leeres Array
    roofAreaOver20: null, // null = nicht ausgewählt, true/false für Ja/Nein
    roofAreaSize: '', // Quadratmeter als String
    hasAlternativeMounting: null,
    alternativeMountingArea: '',
    hasMonumentProtection: null, // null = nicht ausgewählt, true/false für Ja/Nein
    consumptionOver2700: null,
    consumptionInRangeWith3Months: null,
    hasRealizationTimeframe: null,
    hasFutureEnergyConsumers: null,
    yearlyConsumption: '',
    // hasAsbestos: null // DIESES FELD WURDE ENTFERNT
  });

  const [formData, setFormData] = useState({
    produkt: 'PV',
    signature: '',
    dienstleister: user?.full_name || '',
    anrede: '',
    name: '',
    nachname: '',
    email: '',
    handy: '',
    strasse: '',
    hausnummer: '',
    postleitzahl: '',
    stadt: '',
  });

  // Opt-In Toggle State
  const [optInMode, setOptInMode] = useState('signature'); // 'signature' oder 'email'

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEmailWarningModal, setShowEmailWarningModal] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);

  // Fortschritt der progressiven Offenlegung
  const [progression, setProgression] = useState(1);

  const hasQualifyingElectricityCosts = () => isPvConsumptionStepComplete(propertyData);

  // Vorlagen-Logik
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateError, setTemplateError] = useState('');
  const [templateSuccess, setTemplateSuccess] = useState('');
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [templateJustSaved, setTemplateJustSaved] = useState(false);

  // Hilfsfunktion: Progression aus propertyData berechnen
  function calculateProgressionFromPropertyData(propertyData) {
    let p = 1;
    if (propertyData.isOwner !== null) p = 2;
    if (propertyData.buildingType) p = 3;
    if (propertyData.roofOrientation) p = 4;
    if (propertyData.roofAreaOver20 !== null) p = 5;
    if (propertyData.roofAreaOver20 === true && propertyData.roofAreaSize) p = 8;
    if (propertyData.roofAreaOver20 === false && propertyData.hasAlternativeMounting !== null) p = 6;
    if (propertyData.roofAreaOver20 === false && propertyData.hasAlternativeMounting === true && propertyData.alternativeMountingArea) p = 8;
    if (propertyData.roofAreaOver20 === false && propertyData.hasAlternativeMounting === false) p = 8;
    if (propertyData.hasMonumentProtection !== null) p = 9;
    if (propertyData.consumptionOver2700 === true && propertyData.hasRealizationTimeframe !== null) p = 13;
    else if (
      propertyData.consumptionOver2700 === true &&
      isYearlyConsumptionOver2700Valid(propertyData.yearlyConsumption)
    ) p = 12;
    else if (propertyData.consumptionOver2700 === true) p = 9;
    else if (propertyData.consumptionOver2700 === false && propertyData.consumptionInRangeWith3Months !== null) p = 11;
    else if (propertyData.consumptionOver2700 === false) p = 10;
    return p;
  }

  // Organisationsname laden
  useEffect(() => {
    const loadTeamName = async () => {
      if (user && user.id && user.organization_id && user.team_id) {
        try {
          // Sub-Team-Informationen laden
          const subTeamResponse = await getUserSubTeamInfo(user.id);
          let projectName = '';
          
          if (subTeamResponse.success && subTeamResponse.userInfo) {
            const userInfo = subTeamResponse.userInfo;
            
            // Wenn Benutzer in einem Sub-Team ist, zeige "Team-Name (Sub-Team-Name)"
            if (userInfo.sub_team_name) {
              projectName = `${userInfo.team_name} (${userInfo.sub_team_name})`;
            } else {
              // Wenn nur Team, zeige nur Team-Name
              projectName = userInfo.team_name;
            }
          } else {
            // Fallback: Team-Informationen laden
            const response = await getTeamsByOrganization(user.id, user.organization_id);
            if (response.success) {
              const userTeam = response.teams.find(team => team.id === user.team_id);
              if (userTeam) {
                projectName = userTeam.name;
              } else {
                projectName = 'Mein Team';
              }
            } else {
              projectName = user.team?.name || 'Unbekanntes Team';
            }
          }
          
          setTeamName(projectName);
          setFormData(prev => ({
            ...prev,
            projekt: projectName,
            dienstleister: user.full_name || user.email || ''
          }));
        } catch (err) {
          console.error('Fehler beim Laden der Team/Sub-Team-Informationen:', err);
          // Fallback bei Netzwerkfehler
          setTeamName(user.team?.name || 'Mein Team');
          setFormData(prev => ({
            ...prev,
            projekt: user.team?.name || 'Mein Team',
            dienstleister: user.full_name || user.email || ''
          }));
        }
      } else {
        // Fallback: Wenn keine Team-ID vorhanden, versuche Organization zu laden
        await loadOrganizationName();
      }
    };

    const loadOrganizationName = async () => {
      if (user && user.id && user.organization_id) {
        try {
          const response = await getOrganizations(user.id);
          if (response.success) {
            const userOrg = response.organizations.find(org => org.id === user.organization_id);
            if (userOrg) {
              setOrganizationName(userOrg.name);
              // Projekt automatisch mit Organisationsname setzen (Fallback)
              setFormData(prev => ({
                ...prev,
                projekt: userOrg.name,
                dienstleister: user.full_name || user.email || ''
              }));
            }
          }
        } catch (err) {
          console.error('Fehler beim Laden der Organisation:', err);
        }
      }
    };

    loadTeamName();
  }, [user]);

  // Beim Mounten: Vorlagen laden
  useEffect(() => {
    async function fetchTemplates() {
      if (user && user.id) {
        setIsLoadingTemplates(true);
        const res = await getLeadTemplates(user.id);
        if (res.success) {
          setTemplates(res.templates);
          if (res.templates.length > 0) setShowTemplateModal(true);
        }
        setIsLoadingTemplates(false);
      }
    }
    fetchTemplates();
  }, [user]);

  const handleCancel = () => {
    if (customerId) {
      // Zurück zur Kundendetailseite navigieren
      navigate(-1);
    } else if (onCancel) {
      // Standard-onCancel-Handler verwenden
      onCancel();
    } else {
      // Zur Leads-Seite navigieren, wenn kein onCancel-Handler vorhanden ist
      navigate('/dashboard');
    }
  };

  // Handler für die Eigentumsauswahl (Ja/Nein)
  const handleOwnershipChange = (isOwner) => {
    setPropertyData({
      ...propertyData,
      isOwner: isOwner
    });

    // Erhöhe die Progression, wenn Option gewählt wurde
    if (progression === 1) {
      setProgression(2);
    }
  };

  // Handler für die Gebäudeart
  const handleBuildingTypeChange = (type) => {
    setPropertyData({
      ...propertyData,
      buildingType: type
    });

    // Erhöhe die Progression, wenn Option gewählt wurde
    if (progression === 2) {
      setProgression(3);
    }
  };

  // Handler für die Dachausrichtung
  const handleRoofOrientationChange = (orientation) => {
    setPropertyData({
      ...propertyData,
      roofOrientation: orientation // GEÄNDERT: Direkt den String speichern
    });

    // Erhöhe die Progression, wenn Option gewählt wurde
    if (progression === 3) {
      setProgression(4);
    }
  };

  // Handler für Dachfläche über 20qm
  const handleRoofAreaOver20Change = (isOver20) => {
    setPropertyData({
      ...propertyData,
      roofAreaOver20: isOver20,
      // Reset nachfolgende Felder basierend auf der Auswahl
      roofAreaSize: '',
      hasAlternativeMounting: null,
      alternativeMountingArea: ''
    });

    // WICHTIG: Progression immer korrekt setzen, unabhängig vom vorherigen Zustand
    if (isOver20) {
      setProgression(5); // Ja -> Dachflächengröße eingeben
    } else {
      setProgression(6); // Nein -> Alternative Montage
    }
  };

  // Handler für Dachflächengröße Input (wenn Dachfläche > 20qm = Ja)
  const handleRoofAreaSizeChange = (e) => {
    const { value } = e.target;
    setPropertyData({
      ...propertyData,
      roofAreaSize: value
    });

    if (progression === 5 || progression === 6) {
      if (value.trim() !== '' && Number(value) >= 20) {
        setProgression(8); // Direkt zu Denkmalschutz
      } else if (value.trim() !== '' && Number(value) < 20) {
        setProgression(6); // Zu alternativer Montage (falls < 20qm eingegeben)
      }
    }
  };

  // Handler für alternative Montage (wenn Dachfläche > 20qm = Nein)
  const handleAlternativeMountingChange = (hasAlternative) => {
    setPropertyData({
      ...propertyData,
      hasAlternativeMounting: hasAlternative,
      alternativeMountingArea: hasAlternative ? propertyData.alternativeMountingArea : ''
    });

    // Progression immer korrekt setzen
    if (hasAlternative) {
      setProgression(7); // Ja -> Flächengröße eingeben
    } else {
      setProgression(8); // Nein -> Denkmalschutz
    }
  };

  // Handler für alternative Montagefläche
  const handleAlternativeMountingAreaChange = (e) => {
    const { value } = e.target;
    setPropertyData({
      ...propertyData,
      alternativeMountingArea: value
    });

    if (progression === 7 && value.trim() !== '') {
      if (Number(value) >= 20) {
        setProgression(8); // Zu Denkmalschutz
      }
      // Wenn < 20, bleibt bei Progression 7 und zeigt Disqualifizierung
    }
  };

  // Handler für Denkmalschutz
  const handleMonumentProtectionChange = (hasProtection) => {
    setPropertyData({
      ...propertyData,
      hasMonumentProtection: hasProtection
    });

    if (progression === 8) {
      setProgression(9);
    }
  };

  const handleConsumptionOver2700Change = (isOver2700) => {
    const nextData = {
      ...propertyData,
      consumptionOver2700: isOver2700,
      consumptionInRangeWith3Months: isOver2700 ? null : propertyData.consumptionInRangeWith3Months,
      hasRealizationTimeframe: isOver2700 ? propertyData.hasRealizationTimeframe : null,
      yearlyConsumption: isOver2700 ? propertyData.yearlyConsumption : ''
    };
    setPropertyData(nextData);
    if (isOver2700) {
      setProgression(isYearlyConsumptionOver2700Valid(nextData.yearlyConsumption) ? 12 : 9);
    } else {
      setProgression(10);
    }
  };

  const handleYearlyConsumptionChange = (value) => {
    setPropertyData({
      ...propertyData,
      yearlyConsumption: value
    });
    if (propertyData.consumptionOver2700 === true && isYearlyConsumptionOver2700Valid(value)) {
      setProgression(propertyData.hasRealizationTimeframe !== null ? 13 : 12);
    }
  };

  const handleConsumptionInRangeWith3MonthsChange = (value) => {
    setPropertyData({
      ...propertyData,
      consumptionInRangeWith3Months: value
    });
    setProgression(value ? 11 : 10);
  };

  const handleRealizationTimeframeChange = (hasTimeframe) => {
    setPropertyData({
      ...propertyData,
      hasRealizationTimeframe: hasTimeframe
    });
    if (progression === 12) {
      setProgression(13);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Erlaubte Felder erweitern (signature entfernt aus der Liste)
    if (['anrede', 'name', 'nachname', 'email', 'handy', 'strasse', 'hausnummer', 'postleitzahl', 'stadt'].includes(name)) {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  // Neuer Handler für Unterschrift
  const handleSignatureChange = (signatureData) => {
    setFormData({
      ...formData,
      signature: signatureData
    });
  };

  // Opt-In Toggle Handler
  const handleOptInModeChange = (mode) => {
    setOptInMode(mode);
    // Wenn auf E-Mail umgestellt wird, Unterschrift zurücksetzen
    if (mode === 'email') {
      setFormData(prev => ({
        ...prev,
        signature: ''
      }));
    }
  };

  const handleOptInSignatureChange = (signatureData) => {
    setFormData(prev => ({
      ...prev,
      signature: signatureData
    }));
  };

  // Wechsel zum nächsten Schritt
  const nextStep = () => {
    setStep(step + 1);
  };

  // Zurück zum vorherigen Schritt
  const prevStep = () => {
    setStep(step - 1);
  };

  // Funktion zur Überprüfung der effektiven Dachfläche
  const getEffectiveRoofArea = () => {
    // Wenn Dachfläche > 20qm = Ja und Größe eingegeben
    if (propertyData.roofAreaOver20 === true && propertyData.roofAreaSize) {
      return Number(propertyData.roofAreaSize);
    }

    // Wenn Dachfläche > 20qm = Nein aber alternative Montage = Ja und Größe eingegeben
    if (propertyData.roofAreaOver20 === false &&
      propertyData.hasAlternativeMounting === true &&
      propertyData.alternativeMountingArea) {
      return Number(propertyData.alternativeMountingArea);
    }

    return 0;
  };

  // Prüfen, ob alle Pflichtfelder im ersten Schritt ausgefüllt sind
  const isStep1Valid = () => {
    const effectiveArea = getEffectiveRoofArea();

    return (
      propertyData.isOwner !== null &&
      propertyData.buildingType !== '' &&
      // Anpassung: roofOrientation ist jetzt ein String, also prüfen, ob es nicht leer ist
      propertyData.roofOrientation !== '' &&
      propertyData.roofAreaOver20 !== null &&
      // Dachflächen-Validierung
      (
        // Fall 1: Dachfläche > 20qm = Ja und ausreichende Größe
        (propertyData.roofAreaOver20 === true && effectiveArea >= 20) ||
        // Fall 2: Dachfläche > 20qm = Nein, alternative Montage = Ja und ausreichende Größe
        (propertyData.roofAreaOver20 === false &&
          propertyData.hasAlternativeMounting === true &&
          effectiveArea >= 20) ||
        // Fall 3: Dachfläche > 20qm = Nein, alternative Montage = Nein (für Disqualifizierung)
        (propertyData.roofAreaOver20 === false &&
          propertyData.hasAlternativeMounting === false)
      ) &&
      propertyData.hasMonumentProtection !== null &&
      hasQualifyingElectricityCosts()
    );
  };

  // Prüfen, ob Lead qualifiziert ist (entsprechend den Kriterien)
  const isLeadQualified = () => {
    const effectiveArea = getEffectiveRoofArea();

    return (
      propertyData.isOwner === true &&
      propertyData.buildingType !== '' &&
      // ANPASSUNG: roofOrientation ist jetzt ein String, prüfen, ob es NICHT "Nord" ist.
      propertyData.roofOrientation !== 'Nord' &&
      effectiveArea >= 20 && // Entweder Hauptdach oder alternative Montage >= 20qm
      propertyData.hasMonumentProtection === false &&
      hasQualifyingPvConsumption(propertyData)
    );
  };

  // Prüfen, ob Lead disqualifiziert ist (entsprechend den Kriterien)
  const isLeadDisqualified = () => {
    if (propertyData.isOwner === false) return true;
    // ANPASSUNG: Prüfen, ob 'Nord' als String gespeichert ist.
    if (propertyData.roofOrientation === 'Nord') return true;
    if (propertyData.hasMonumentProtection === true) return true;

    // Dachflächen-Disqualifizierung
    const effectiveArea = getEffectiveRoofArea();
    if (propertyData.roofAreaOver20 === false && propertyData.hasAlternativeMounting === false) return true;
    if (effectiveArea > 0 && effectiveArea < 20) return true;

    if (isPvConsumptionDisqualified(propertyData)) return true;

    return false;
  };

  // Funktion zum Generieren der Template-Email
  const generateTemplateEmail = (vorname, nachname) => {
    const cleanVorname = (vorname || '').toLowerCase().replace(/[^a-zäöüß]/g, '');
    const cleanNachname = (nachname || '').toLowerCase().replace(/[^a-zäöüß]/g, '');
    if (cleanVorname && cleanNachname) {
      return `${cleanVorname}.${cleanNachname}@keinemail.de`;
    }
    return '';
  };

  // Absenden des Formulars mit allen Daten
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Prüfen ob Email leer ist für PV/WP/PV+WP Leads
    const isPVOrWP = formData.produkt === 'PV' || formData.produkt === 'WP' || formData.produkt === 'PV+WP';
    if (isPVOrWP && !formData.email && formData.name && formData.nachname) {
      // Email ist leer, Warnung anzeigen
      setPendingSubmit(e);
      setShowEmailWarningModal(true);
      return;
    }

    // Wenn Email leer ist aber keine Vor- und Nachnamen vorhanden, direkt absenden
    await submitLeadData();
  };

  // Eigentliche Submit-Funktion
  const submitLeadData = async (emailOverride = null) => {
    setLoading(true);

    try {
      // Kombiniere alle Daten für Lead (inklusive Customer-Status)
      const leadData = {
        userId: user.id,
        ...formData,
        // Email-Override falls vorhanden (für Template-Email)
        email: emailOverride !== null ? emailOverride : formData.email,
        // Immobiliendaten als zusätzliche Felder
        isOwner: propertyData.isOwner,
        buildingType: propertyData.buildingType,
        roofOrientation: propertyData.roofOrientation, // Hier wird der String übergeben
        roofAreaOver20: propertyData.roofAreaOver20,
        roofAreaSize: propertyData.roofAreaSize,
        hasAlternativeMounting: propertyData.hasAlternativeMounting,
        alternativeMountingArea: propertyData.alternativeMountingArea,
        hasMonumentProtection: propertyData.hasMonumentProtection,
        ...getPvConsumptionSubmitValues(propertyData),
        hasFutureEnergyConsumers: propertyData.hasFutureEnergyConsumers,
        isQualified: isLeadQualified(),
        // Customer-Status setzen
        customerStatus: 'Erfasst'
      };

      // LEADADATA.HASAGREEDTOCONTACT ENTFERNEN, DA KEINE CHECKBOX
      // hasAgreedToContact: hasAgreedToContact, // DIESE ZEILE WIRD ENTFERNT

      // Lead hinzufügen (erstellt automatisch den Customer-Status)
      const leadResponse = await addLead(leadData);

      if (!leadResponse.success) {
        setError(leadResponse.error || 'Fehler beim Hinzufügen des Leads');
        return;
      }

      setSuccess('Lead und Kunde erfolgreich hinzugefügt!');

      // Opt-In E-Mail senden, wenn E-Mail-Modus gewählt wurde
      if (optInMode === 'email' && formData.email) {
        try {
          const emailResponse = await sendEmail({
            emailType: 'optin_confirmation',
            recipientEmail: formData.email,
            recipientName: formData.name,
            userId: user.id,
            organizationId: user.organization_id || 4
          });

          if (emailResponse.success) {
            setSuccess('Lead erfolgreich hinzugefügt und Opt-In E-Mail gesendet!');
          } else {
            setSuccess('Lead erfolgreich hinzugefügt, aber Opt-In E-Mail konnte nicht gesendet werden: ' + emailResponse.error);
          }
        } catch (emailError) {
          setSuccess('Lead erfolgreich hinzugefügt, aber Opt-In E-Mail konnte nicht gesendet werden: ' + emailError.message);
        }
      }

      // Lead-Liste aktualisieren
      if (onLeadAdded) {
        onLeadAdded();
      }

      // Nach 2 Sekunden zur Leads-Liste zurückkehren
      setTimeout(() => {
        if (customerId) {
          navigate(-1);
        } else if (onCancel) {
          onCancel();
        } else {
          navigate('/dashboard');
        }
      }, 2000);

    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Rendering des ersten Schritts - PV Anlage Daten mit progressiver Offenlegung
  const renderStep1 = () => {
    return (
      <div className="pv-form-step">
        <h2 className="form-title">PV Anlage</h2>

        {/* Eigentümer - Immer sichtbar als erstes Element */}
        <div className="form-section">
          <label className="form-label required">Eigentümer</label>
          <div className="yes-no-buttons">
            <button
              type="button"
              className={`yes-button ${propertyData.isOwner === true ? 'selected' : ''}`}
              onClick={() => handleOwnershipChange(true)}
            >
              <div className="icon-container">
                <svg viewBox="0 0 24 24" className="yes-icon">
                  <path
                    d="M4 12.6L9 17.6L20 6.6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span>Ja</span>
            </button>
            <button
              type="button"
              className={`no-button ${propertyData.isOwner === false ? 'selected' : ''}`}
              onClick={() => handleOwnershipChange(false)}
            >
              <div className="icon-container">
                <svg viewBox="0 0 24 24" className="no-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span>Nein</span>
            </button>
          </div>

          {/* Qualifizierungsnachricht für Eigentümer */}
          {propertyData.isOwner === false && (
            <div className="qualification-message disqualified">
              <svg viewBox="0 0 24 24" className="qualification-icon">
                <path
                  d="M5 5L19 19M5 19L19 5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Lead nicht Qualifiziert</span>
            </div>
          )}

        </div>

        {/* Gebäudeart - Zeige nur, wenn Eigentümer ausgewählt ist */}
        {progression >= 2 && (
          <div className="form-section">
            <label className="form-label required">Gebäudeart</label>
            <div className="building-type-buttons">
              <button
                type="button"
                className={`building-button ${propertyData.buildingType === 'Einfamilienhaus' ? 'selected' : ''}`}
                onClick={() => handleBuildingTypeChange('Einfamilienhaus')}
              >
                <div className="building-icon">
                  <svg viewBox="0 0 100 100">
                    <path d="M50,20 L20,45 L20,80 L80,80 L80,45 Z" fill="none" stroke="currentColor" strokeWidth="3" />
                    <rect x="35" y="50" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="55" y="50" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="45" y="65" width="10" height="15" fill="none" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <span>Einfamilienhaus</span>
              </button>
              <button
                type="button"
                className={`building-button ${propertyData.buildingType === 'Zweifamilienhaus' ? 'selected' : ''}`}
                onClick={() => handleBuildingTypeChange('Zweifamilienhaus')}
              >
                <div className="building-icon">
                  <svg viewBox="0 0 100 100">
                    <path d="M50,20 L15,45 L15,80 L85,80 L85,45 Z" fill="none" stroke="currentColor" strokeWidth="3" />
                    <line x1="50" y1="45" x2="50" y2="80" stroke="currentColor" strokeWidth="2" />
                    <rect x="25" y="50" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="65" y="50" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="30" y="65" width="10" height="15" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="60" y="65" width="10" height="15" fill="none" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <span>Zweifamilienhaus</span>
              </button>
              <button
                type="button"
                className={`building-button ${propertyData.buildingType === 'Reihenhaus' ? 'selected' : ''}`}
                onClick={() => handleBuildingTypeChange('Reihenhaus')}
              >
                <div className="building-icon">
                  <svg viewBox="0 0 100 100">
                    <path d="M10,80 L10,45 L30,30 L30,80 Z M30,80 L30,30 L50,30 L50,80 Z M50,80 L50,30 L70,30 L70,80 Z M70,80 L70,30 L90,45 L90,80 Z" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="15" y="60" width="8" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="37" y="60" width="8" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="56" y="60" width="8" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="77" y="60" width="8" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </div>
                <span>Reihenhaus</span>
              </button>
            </div>
          </div>
        )}

        {progression >= 3 && (
          <div className="form-section">
            <label className="form-label required">Dachausrichtung</label>
            <div className="roof-orientation-buttons">
              <button
                type="button"
                className={`direction-button ${propertyData.roofOrientation === 'West' ? 'selected' : ''}`}
                onClick={() => handleRoofOrientationChange('West')}
              >
                <div className="direction-icon">
                  <svg viewBox="0 0 24 24">
                    <path d="M4 12h12M4 12l5 -5M4 12l5 5" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span>West</span>
              </button>
              <button
                type="button"
                className={`direction-button ${propertyData.roofOrientation === 'Ost' ? 'selected' : ''}`}
                onClick={() => handleRoofOrientationChange('Ost')}
              >
                <div className="direction-icon">
                  <svg viewBox="0 0 24 24">
                    <path d="M20 12H8M20 12l-5 -5M20 12l-5 5" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span>Ost</span>
              </button>
              <button
                type="button"
                className={`direction-button ${propertyData.roofOrientation === 'Nord' ? 'selected' : ''}`}
                onClick={() => handleRoofOrientationChange('Nord')}
              >
                <div className="direction-icon">
                  <svg viewBox="0 0 24 24">
                    <path d="M12 4v12M12 4l-5 5M12 4l5 5" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span>Nord</span>
              </button>
              <button
                type="button"
                className={`direction-button ${propertyData.roofOrientation === 'Süd' ? 'selected' : ''}`}
                onClick={() => handleRoofOrientationChange('Süd')}
              >
                <div className="direction-icon">
                  <svg viewBox="0 0 24 24">
                    <path d="M12 20V8M12 20l-5 -5M12 20l5 -5" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span>Süd</span>
              </button>
            </div>

            {/* Qualifizierungsnachricht für Dachausrichtung */}
            {propertyData.roofOrientation === 'Nord' && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}

        {/* Dachfläche über 20QM */}
        {progression >= 4 && (
          <div className="form-section">
            <label className="form-label required">Dachfläche über 20QM</label>
            <div className="yes-no-buttons">
              <button
                type="button"
                className={`yes-button ${propertyData.roofAreaOver20 === true ? 'selected' : ''}`}
                onClick={() => handleRoofAreaOver20Change(true)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="yes-icon">
                    <path
                      d="M4 12.6L9 17.6L20 6.6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Ja</span>
              </button>
              <button
                type="button"
                className={`yes-button ${propertyData.roofAreaOver20 === false ? 'selected' : ''}`}
                onClick={() => handleRoofAreaOver20Change(false)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="no-icon">
                    <path
                      d="M5 5L19 19M5 19L19 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Nein</span>
              </button>
            </div>
          </div>
        )}

        {/* Dachflächengröße - nur wenn über 20qm = JA */}
        {progression >= 5 && propertyData.roofAreaOver20 === true && (
          <div className="form-section">
            <label className="form-label required">Wie viele QM?</label>
            <input
              type="number"
              name="roofAreaSize"
              value={propertyData.roofAreaSize}
              onChange={handleRoofAreaSizeChange}
              className="form-input"
              placeholder="z.B. 40"
              required
            />

            {/* Qualifizierungsnachricht für Dachflächengröße */}
            {Number(propertyData.roofAreaSize) < 20 && propertyData.roofAreaSize !== '' && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}

        {/* Alternative Montage - nur wenn über 20qm = NEIN */}
        {progression >= 6 && propertyData.roofAreaOver20 === false && (
          <div className="form-section">
            <label className="form-label required">Alternative Montage möglich (Carport, Garage)?</label>
            <div className="yes-no-buttons">
              <button
                type="button"
                className={`yes-button ${propertyData.hasAlternativeMounting === true ? 'selected' : ''}`}
                onClick={() => handleAlternativeMountingChange(true)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="yes-icon">
                    <path
                      d="M4 12.6L9 17.6L20 6.6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Ja</span>
              </button>
              <button
                type="button"
                className={`no-button ${propertyData.hasAlternativeMounting === false ? 'selected' : ''}`}
                onClick={() => handleAlternativeMountingChange(false)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="no-icon">
                    <path
                      d="M5 5L19 19M5 19L19 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Nein</span>
              </button>
            </div>

            {/* Qualifizierungsnachricht wenn keine alternative Montage */}
            {propertyData.hasAlternativeMounting === false && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}

        {/* Alternative Montagefläche - nur wenn alternative Montage = JA */}
        {progression >= 7 && propertyData.roofAreaOver20 === false && propertyData.hasAlternativeMounting === true && (
          <div className="form-section">
            <label className="form-label required">Wie viele QM alternative Montagefläche?</label>
            <input
              type="number"
              name="alternativeMountingArea"
              value={propertyData.alternativeMountingArea}
              onChange={handleAlternativeMountingAreaChange}
              className="form-input"
              placeholder="z.B. 25"
              required
            />

            {/* Qualifizierungsnachricht für alternative Montagefläche */}
            {Number(propertyData.alternativeMountingArea) < 20 && propertyData.alternativeMountingArea !== '' && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}

        {/* Denkmalschutz */}
        {progression >= 8 && (
          <div className="form-section">
            <label className="form-label required">Denkmalschutz / Asbest</label>
            <div className="yes-no-buttons">
              <button
                type="button"
                className={`yes-button reverse-yes-button ${propertyData.hasMonumentProtection === true ? 'selected' : ''}`}
                onClick={() => handleMonumentProtectionChange(true)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="yes-icon">
                    <path
                      d="M4 12.6L9 17.6L20 6.6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Ja</span>
              </button>
              <button
                type="button"
                className={`reverse-no-button ${propertyData.hasMonumentProtection === false ? 'selected' : ''}`}
                onClick={() => handleMonumentProtectionChange(false)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="no-icon">
                    <path
                      d="M5 5L19 19M5 19L19 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Nein</span>
              </button>
            </div>

            {/* Qualifizierungsnachricht für Denkmalschutz */}
            {propertyData.hasMonumentProtection === true && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}

        <PvConsumptionQuestions
          data={propertyData}
          progression={progression}
          onOver2700Change={handleConsumptionOver2700Change}
          onYearlyConsumptionChange={handleYearlyConsumptionChange}
          onRangeWith3MonthsChange={handleConsumptionInRangeWith3MonthsChange}
          onRealizationTimeframeChange={handleRealizationTimeframeChange}
          showQualifiedBanner
          isQualified={isLeadQualified()}
          isDisqualified={isLeadDisqualified()}
          isStepValid={isStep1Valid()}
        />

        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={handleCancel}>
            Abbrechen
          </button>
          <button
            type="button"
            className="next-button"
            onClick={nextStep}
            disabled={!isStep1Valid() || isLeadDisqualified()}
          >
            Weiter
          </button>
        </div>
        
      </div>
    );
  };

  // Rendering des zweiten Schritts - Kundendaten
  const renderStep2 = () => {
    return (
      <>
        <h3>Kundendaten</h3>

        <div className="form-row">
          <div className="form-field">
            <label>Anrede*</label>
            <select
              name="anrede"
              value={formData.anrede}
              onChange={handleChange}
              required
            >
              <option value="">Bitte wählen</option>
              <option value="herr">Herr</option>
              <option value="frau">Frau</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>Vorname*</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-field">
            <label>Nachname*</label>
            <input
              type="text"
              name="nachname"
              value={formData.nachname}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>E-Mail</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
          </div>
          <div className="form-field">
            <label>Handy*</label>
            <input
              type="tel"
              name="handy"
              value={formData.handy}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>Straße*</label>
            <input
              type="text"
              name="strasse"
              value={formData.strasse}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-field">
            <label>Hausnummer*</label>
            <input
              type="text"
              name="hausnummer"
              value={formData.hausnummer}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>Postleitzahl*</label>
            <input
              type="text"
              name="postleitzahl"
              value={formData.postleitzahl}
              onChange={handleChange}
              pattern="[0-9]{5}"
              maxLength="5"
              required
            />
          </div>
          <div className="form-field">
            <label>Stadt*</label>
            <input
              type="text"
              name="stadt"
              value={formData.stadt}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {/* NEU PLATZIERT: Bereich für die Einwilligung - JETZT OHNE CHECKBOX */}
        <div className="form-section consent-section">
          <div className="consent-text">
            <p>Bestätigung der Einwilligung zur telefonischen oder persönlichen Kontaktaufnahme durch {getConsentBranding(user).companyName} oder deren Vertragspartner zur Datenerhebung, -verarbeitung und Informationszusendung. Die Datenschutzinformationen der {getConsentBranding(user).companyName} sind hier einzusehen: <a href={getConsentBranding(user).datenschutzUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1d72b8', wordBreak: 'break-all' }}>{getConsentBranding(user).datenschutzUrl}</a> <span style={{ color: 'red' }}>*</span></p>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <OptInToggle
              mode={optInMode}
              onModeChange={handleOptInModeChange}
              signature={formData.signature}
              onSignatureChange={handleOptInSignatureChange}
              email={formData.email}
              required={true}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            {templates.length < 5 && !templateJustSaved ? (
              <button type="button" onClick={handleSaveTemplate}>
                Als Vorlage speichern
              </button>
            ) : null}
            {templateJustSaved && (
              <span style={{ color: '#1976d2', fontSize: '0.95em' }}>
                Vorlage gespeichert!
              </span>
            )}
            {templates.length >= 5 && !templateJustSaved && (
              <span style={{ color: '#888', fontSize: '0.95em' }}>
                Maximal 5 Vorlagen erlaubt
              </span>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={prevStep}>
            Zurück
          </button>
          <button type="button" className="cancel-button" onClick={handleCancel}>
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !formData.name || !formData.strasse || !formData.hausnummer || !formData.postleitzahl || !formData.stadt || (optInMode === 'signature' && !formData.signature)}
          >
            {loading ? 'Wird gespeichert...' : 'Lead speichern'}
          </button>
        </div>
      </>
    );
  };

  // Handler für Auswahl einer Vorlage
  const handleTemplateSelect = (templateId) => {
    const tpl = templates.find(t => t.id == templateId);
    if (tpl) {
      try {
        const data = JSON.parse(tpl.data);
        setPropertyData(data.propertyData || {});
        setFormData(prev => ({
          ...prev,
          strasse: data.formData?.strasse || '',
          hausnummer: data.formData?.hausnummer || '',
          postleitzahl: data.formData?.postleitzahl || '',
          stadt: data.formData?.stadt || ''
        }));
        // Progression setzen
        const newProg = calculateProgressionFromPropertyData(data.propertyData || {});
        setProgression(newProg);
      } catch (e) {
        setTemplateError('Vorlage konnte nicht geladen werden.');
      }
    }
    setShowTemplateModal(false);
  };

  // Handler für Fortfahren ohne Vorlage
  const handleTemplateCancel = () => {
    setShowTemplateModal(false);
  };

  // Handler für Löschen einer Vorlage
  const handleTemplateDelete = async (templateId) => {
    if (!window.confirm('Vorlage wirklich löschen?')) return;
    const res = await deleteLeadTemplate(user.id, templateId);
    if (res.success) {
      setTemplates(templates.filter(t => t.id != templateId));
      setTemplateSuccess('Vorlage gelöscht.');
    } else {
      setTemplateError(res.error || 'Fehler beim Löschen');
    }
  };

  // Handler für Vorlage speichern
  const handleSaveTemplate = async () => {
    // Automatische Namensvergabe: 'Vorlage 1' bis 'Vorlage 5', lückenlos
    const usedNumbers = templates
      .map(t => {
        const match = t.name.match(/^Vorlage (\d)$/);
        return match ? parseInt(match[1], 10) : null;
      })
      .filter(n => n !== null)
      .sort((a, b) => a - b);
    let freieNummer = 1;
    for (let i = 1; i <= 5; i++) {
      if (!usedNumbers.includes(i)) {
        freieNummer = i;
        break;
      }
    }
    const name = `Vorlage ${freieNummer}`;
    const type = formData.produkt || 'PV';
    const data = {
      propertyData,
      formData: {
        strasse: formData.strasse,
        hausnummer: formData.hausnummer,
        postleitzahl: formData.postleitzahl,
        stadt: formData.stadt
      }
    };
    const res = await saveLeadTemplate(user.id, name, type, data);
    if (res.success) {
      setTemplateSuccess('Vorlage gespeichert!');
      setTemplateJustSaved(true);
      // Neu laden
      const reload = await getLeadTemplates(user.id);
      if (reload.success) setTemplates(reload.templates);
    } else {
      setTemplateError(res.error || 'Fehler beim Speichern');
    }
  };

  return (
    <div className="lead-form">
      {showTemplateModal && templates.length > 0 && (
        <LeadTemplateModal
          templates={templates}
          onSelect={handleTemplateSelect}
          onCancel={handleTemplateCancel}
          onDelete={handleTemplateDelete}
        />
      )}
      {templateError && <div className="error-message">{templateError}</div>}
      {templateSuccess && <div className="success-message">{templateSuccess}</div>}
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      <form onSubmit={handleSubmit}>
        {step === 1 ? renderStep1() : renderStep2()}
      </form>

      {/* Email-Warnung Modal */}
      {showEmailWarningModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '16px',
          overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: window.innerWidth <= 768 ? '20px 16px' : '24px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ 
              marginTop: 0, 
              marginBottom: '16px', 
              color: '#d32f2f',
              fontSize: window.innerWidth <= 768 ? '18px' : '20px',
              lineHeight: '1.4'
            }}>
              ⚠️ E-Mail-Adresse ist leer
            </h3>
            <p style={{ 
              marginBottom: '20px', 
              color: '#666',
              fontSize: window.innerWidth <= 768 ? '14px' : '16px',
              lineHeight: '1.5'
            }}>
              Die E-Mail-Adresse ist leer. Möchten Sie diese automatisch mit einem Template füllen?
            </p>
            <p style={{ 
              marginBottom: '20px', 
              padding: '12px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '4px', 
              fontFamily: 'monospace',
              fontSize: window.innerWidth <= 768 ? '12px' : '14px',
              wordBreak: 'break-all',
              overflowWrap: 'break-word'
            }}>
              {generateTemplateEmail(formData.name, formData.nachname)}
            </p>
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end',
              flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
              flexWrap: 'wrap'
            }}>
              <button
                type="button"
                onClick={() => {
                  setShowEmailWarningModal(false);
                  setPendingSubmit(null);
                }}
                style={{
                  padding: window.innerWidth <= 768 ? '12px 16px' : '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                  width: window.innerWidth <= 768 ? '100%' : 'auto',
                  flex: window.innerWidth <= 768 ? '1' : 'none'
                }}
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={async () => {
                  // Email mit Template füllen
                  const templateEmail = generateTemplateEmail(formData.name, formData.nachname);
                  // Email im State aktualisieren
                  setFormData(prev => ({ ...prev, email: templateEmail }));
                  setShowEmailWarningModal(false);
                  // Submit fortsetzen mit Template-Email
                  await submitLeadData(templateEmail);
                }}
                style={{
                  padding: window.innerWidth <= 768 ? '12px 16px' : '10px 20px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                  width: window.innerWidth <= 768 ? '100%' : 'auto',
                  flex: window.innerWidth <= 768 ? '1' : 'none'
                }}
              >
                Ja, mit Template füllen
              </button>
              <button
                type="button"
                onClick={async () => {
                  setShowEmailWarningModal(false);
                  // Submit ohne Email fortsetzen
                  await submitLeadData();
                }}
                style={{
                  padding: window.innerWidth <= 768 ? '12px 16px' : '10px 20px',
                  backgroundColor: '#d32f2f',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                  width: window.innerWidth <= 768 ? '100%' : 'auto',
                  flex: window.innerWidth <= 768 ? '1' : 'none'
                }}
              >
                Nein, ohne E-Mail speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeadForm;
